import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  PauseIcon,
  ArrowPathIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import {
  RECOMMENDED_AUTO_SOURCE_GATES,
  DEFAULT_DEEP_LINK_PLATFORM_HOSTS,
  normalizeDeepLinkPlatformHost,
  formatAutoSourceBand,
  formatFollowerCount,
  prettyProfileUrl,
} from '../constants/scraperDefaults';

const SEARCH_PRODUCTS = ['Top', 'Latest', 'People', 'Media'];

const SOURCE_MODES = [
  { id: 'keyword', label: 'From keywords' },
  { id: 'fyp', label: 'From FYP' },
  { id: 'following', label: 'From follow-backs' },
];

/** Mirrors SCRAPE_V2_FYP_REFRESH_PAGES / _REST_MS on the server, for copy only. */
const FYP_REFRESH_PAGES_HINT = 5;
const FYP_DEFAULT_REST_MINUTES = 10;
const POLL_MS = 20_000;

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

function relTime(value) {
  if (!value) return 'never';
  const diff = Date.now() - new Date(value).getTime();
  if (diff < 60_000) return 'just now';
  const mins = Math.round(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * Finished follow-backs stored in the ledger. In-flight seeds are listed
 * separately so adding source accounts does not inflate this number.
 */
function followBacksPosition(keyword) {
  return Number(keyword.followBacksDone) || 0;
}

/** "in 12m" for a timestamp in the future; relTime only reads backwards. */
function untilTime(value) {
  const ms = new Date(value).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 'shortly';
  const mins = Math.ceil(ms / 60_000);
  return mins < 60 ? `in ${mins}m` : `in ${Math.round(mins / 60)}h`;
}

const STATUS_STYLE = {
  active: 'text-emerald-600 border-emerald-500/40 bg-emerald-500/10',
  exhausted: 'text-amber-600 border-amber-500/40 bg-amber-500/10',
  error: 'text-red-600 border-red-500/40 bg-red-500/10',
  disabled: 'text-slate-500 border-slate-400/40 bg-slate-500/10',
};

function StatusPill({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${STATUS_STYLE[status] || STATUS_STYLE.disabled}`}>
      {status}
    </span>
  );
}

/** One keyword row: live counters plus the controls to steer it mid-run. */
function KeywordRow({
  keyword,
  busy,
  pageBudget,
  accounts = [],
  workspaces = [],
  onEdit,
  onToggle,
  onReset,
  onDelete,
  onLoadAuthors,
}) {
  const isFyp = keyword.mode === 'fyp';
  const isFollowing = keyword.mode === 'following';
  const labelOnly = isFyp || isFollowing;
  // A feed's budget drops after its first pass, so trust the row over the
  // panel-wide figure, which only describes keywords.
  const budget = keyword.pageBudget || pageBudget;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(keyword.keyword);
  const [authorsOpen, setAuthorsOpen] = useState(false);
  const [poolOpen, setPoolOpen] = useState(false);
  const [authors, setAuthors] = useState(null);
  const [authorsLoading, setAuthorsLoading] = useState(false);
  const [authorsError, setAuthorsError] = useState('');

  const saveEdit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== keyword.keyword) onEdit(keyword, { keyword: next });
    else setDraft(keyword.keyword);
  };

  // A running keyword keeps finding accounts, so every open refetches rather
  // than serving whatever the first open happened to catch. The sequence guard
  // drops a slow earlier response so it cannot land on top of a newer one.
  const authorsRequest = useRef(0);
  const toggleAuthors = async () => {
    if (authorsOpen) { setAuthorsOpen(false); return; }
    setAuthorsOpen(true);
    const seq = authorsRequest.current + 1;
    authorsRequest.current = seq;
    setAuthorsLoading(true);
    setAuthorsError('');
    try {
      const fresh = await onLoadAuthors(keyword);
      if (authorsRequest.current !== seq) return;
      setAuthors(fresh);
    } catch (err) {
      if (authorsRequest.current !== seq) return;
      setAuthorsError(err?.response?.data?.error || err.message || 'Could not load accounts');
    } finally {
      if (authorsRequest.current === seq) setAuthorsLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        {labelOnly ? (
          // FYP / Following labels are not search phrases.
          <span className="flex-1 min-w-[12rem] text-sm font-medium text-gray-900 dark:text-white truncate">
            {keyword.keyword}
          </span>
        ) : editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') { setDraft(keyword.keyword); setEditing(false); }
            }}
            className="flex-1 min-w-[12rem] text-sm px-2 py-1 rounded border border-indigo-400 bg-white dark:bg-gray-900"
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            title="Click to edit — editing restarts the search from page 1"
            className="flex-1 min-w-[12rem] text-left text-sm font-medium text-gray-900 dark:text-white hover:text-indigo-600 truncate"
          >
            {keyword.keyword}
          </button>
        )}

        <span className="px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-600 text-[10px] font-semibold">
          {keyword.tag}
        </span>
        <span className={`text-[10px] ${isFyp || isFollowing ? 'px-1.5 py-0.5 rounded-full font-semibold' : 'text-gray-400'} ${
          isFollowing ? 'bg-teal-500/10 text-teal-700 dark:text-teal-300' : isFyp ? 'bg-violet-500/10 text-violet-600' : ''
        }`}>
          {isFollowing ? 'Following' : isFyp ? 'For You' : keyword.product}
        </span>
        <span
          className="text-[10px] text-gray-400"
          title={isFyp ? 'Account whose For You feed is being read' : isFollowing ? 'Click to pick which source accounts fetch this walk' : 'Source account running this search'}
        >
          {isFollowing ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setPoolOpen((v) => !v)}
              className="underline decoration-dotted underline-offset-2 hover:text-indigo-600 disabled:opacity-50"
            >
              {(keyword.followingAccountIds || []).length
                ? `${keyword.followingAccountIds.length} source account${keyword.followingAccountIds.length === 1 ? '' : 's'}`
                : 'all source accounts'}
            </button>
          ) : (
            keyword.accountUsername ? `@${keyword.accountUsername}` : 'all source accounts'
          )}
        </span>
        <StatusPill status={keyword.status} />

        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => onToggle(keyword)}
            title={keyword.enabled ? 'Pause this keyword' : 'Resume this keyword'}
            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
          >
            {keyword.enabled
              ? <PauseIcon className="w-3.5 h-3.5" />
              : <PlayIcon className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onReset(keyword)}
            title={keyword.mode === 'following'
              ? 'Restart follow-backs from 0 — forgets finished people so the walk can run again'
              : 'Reset cursor — start the search again from page 1'}
            className="p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete(keyword)}
            title="Retire this keyword (kept in history)"
            className="p-1.5 rounded-lg border border-red-300 text-red-600 disabled:opacity-50"
          >
            <TrashIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {isFollowing && (
        <label className="mt-2 block text-[11px] text-gray-500 dark:text-gray-400">
          Ledger
          <select
            disabled={busy}
            value={keyword.followbackLedgerWorkspaceId || 'legacy'}
            onChange={(e) => onEdit(keyword, { followbackLedgerWorkspaceId: e.target.value })}
            className="ml-2 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          >
            {(workspaces.length ? workspaces : [{ workspaceId: 'legacy', name: 'Current data (legacy)', followBacks: 0 }]).map((w) => (
              <option key={w.workspaceId} value={w.workspaceId}>
                {w.name} — {(Number(w.followBacks) || 0).toLocaleString()} follow-backs
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
        <span><b className="text-gray-700 dark:text-gray-200">{fmt(keyword.discoveredNew)}</b> new sources</span>
        <button
          type="button"
          onClick={toggleAuthors}
          title={`${fmt(keyword.discovered)} sightings in total — click to list the distinct accounts`}
          disabled={busy}
          className="underline decoration-dotted underline-offset-2 hover:text-indigo-600"
        >
          {fmt(keyword.uniqueAuthors)} accounts seen{keyword.authorsTruncated ? '+' : ''}
        </button>
        <span title="Sightings that were an account turning up again — mostly cursor resets replaying the top results">
          {fmt(keyword.duplicateSightings)} duplicates
        </span>
        <span
          className={keyword.hasCursor ? 'text-emerald-600' : ''}
          title={keyword.hasCursor
            ? (isFollowing
              ? (budget
                ? `Paging this follow-back’s Following list — moves on after ${budget} page(s)`
                : 'Paging this follow-back’s Following list')
              : 'Following the cursor deeper into the results')
            : `Next run starts from page 1${isFyp ? ' of the refreshed feed' : isFollowing ? ' of the next follow-back' : ''}`}
        >
          {keyword.hasCursor
            ? (isFollowing
              ? `page ${fmt(
                (keyword.followingInFlight || []).reduce((m, s) => Math.max(m, Number(s.pagesOnSeed) || 0), 0)
                || Number(keyword.followingSeed?.pagesOnSeed) || 0
              )}${budget ? `/${budget}` : ''} of this Following list`
              : `page ${fmt(keyword.pagesFetched)}${budget ? `/${budget}` : ''} · following cursor`)
            : (isFollowing ? 'next follow-back' : isFyp ? 'top of feed' : 'fresh search')}
        </span>
        <span>{fmt(keyword.cursorResets)} restarts</span>
        {isFollowing && (
          <span
            title="Finished follow-backs stored for this row. Reset forgets them and starts the 0/N walk again. In-flight people are listed next to this count."
          >
            <b className="text-gray-700 dark:text-gray-200">{fmt(followBacksPosition(keyword))}</b>
            {' / '}
            {keyword.followBacksTotal == null ? '…' : fmt(keyword.followBacksTotal)}
            {' follow-backs'}
          </span>
        )}
        {isFollowing && (keyword.followingInFlight || []).length > 0 && (
          (keyword.followingInFlight || []).map((seed) => (
            <span key={`${seed.username}:${seed.accountUsername}`} title="In-flight follow-back — this source account will not take another until this list is finished">
              @{seed.username}
              {seed.accountUsername ? ` · @${seed.accountUsername}` : ''}
              {seed.pagesOnSeed ? ` · ${fmt(seed.pagesOnSeed)} pages` : ''}
            </span>
          ))
        )}
        {isFollowing && !(keyword.followingInFlight || []).length && keyword.followingSeed?.username && (
          <span title="Current follow-back whose Following list is being sampled">
            seed @{keyword.followingSeed.username}
            {keyword.followingSeed.pagesOnSeed
              ? ` · ${fmt(keyword.followingSeed.pagesOnSeed)} pages`
              : ''}
          </span>
        )}
        {isFollowing && (
          <span><b className="text-gray-700 dark:text-gray-200">{fmt(keyword.heldCount)}</b> held</span>
        )}
        <span>ran {relTime(keyword.lastRunAt)}</span>
      </div>

      {isFollowing && poolOpen && (
        <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-2">
          <p className="mb-1.5 text-[11px] text-gray-600 dark:text-gray-300">
            Tick who fetches this walk. Changes apply on the next sweep — finished follow-backs stay skipped.
          </p>
          <div className="max-h-40 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
            {(accounts || []).filter((a) => a.status === 'active' || !a.status).map((a) => {
              const selected = new Set((keyword.followingAccountIds || []).map(String));
              const emptyMeansAll = selected.size === 0;
              const checked = emptyMeansAll || selected.has(String(a.id));
              return (
                <label
                  key={a.id}
                  className="flex items-center gap-2 px-2 py-1 text-[11px] cursor-pointer hover:bg-white/60 dark:hover:bg-gray-800/60"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={() => {
                      const next = new Set(emptyMeansAll
                        ? (accounts || []).filter((x) => x.status === 'active' || !x.status).map((x) => String(x.id))
                        : selected);
                      if (next.has(String(a.id))) next.delete(String(a.id));
                      else next.add(String(a.id));
                      if (!next.size) {
                        toast.error('Keep at least one source account');
                        return;
                      }
                      onEdit(keyword, { followingAccountIds: [...next] });
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-gray-700 dark:text-gray-200">@{a.username}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {authorsOpen && (
        <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-2">
          {authorsLoading && (
            <p className="text-[11px] text-gray-500">{authors ? 'Refreshing…' : 'Loading accounts…'}</p>
          )}
          {authorsError && <p className="text-[11px] text-red-600">{authorsError}</p>}
          {authors && (
            <>
              <div className="mb-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                <b>{fmt(authors.uniqueAuthors)}</b> distinct account(s) from {fmt(authors.sightingsTracked)} sighting(s)
                {' · '}<span className="text-amber-600">{fmt(authors.duplicateSightings)} duplicate sighting(s)</span>
                {authors.total > authors.authors.length && (
                  <span className="text-gray-400"> · showing first {fmt(authors.authors.length)}</span>
                )}
                {authors.truncated && (
                  <span className="text-gray-400"> · tracking capped, the real total is higher</span>
                )}
                {authors.sightings > authors.sightingsTracked && (
                  <span className="text-gray-400">
                    {' '}· {fmt(authors.sightings)} lifetime sightings, counted before this list was kept
                  </span>
                )}
              </div>
              <div className="max-h-48 overflow-y-auto flex flex-wrap gap-1">
                {authors.authors.map((a) => (
                  <a
                    key={a.username}
                    href={`https://x.com/${a.username}`}
                    target="_blank"
                    rel="noreferrer"
                    title={a.queuedAsSource
                      ? 'Queued as a source for this session'
                      : 'Seen in results but not queued — already known, or filtered out'}
                    className={`px-1.5 py-0.5 rounded text-[10px] border ${
                      a.queuedAsSource
                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600'
                        : 'border-gray-300 dark:border-gray-600 text-gray-500'
                    }`}
                  >
                    @{a.username}
                  </a>
                ))}
              </div>
              {!authors.authors.length && (
                <p className="text-[11px] text-gray-500">
                  No accounts recorded yet — counts start from this keyword's next search.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {keyword.restingUntil && !keyword.rateLimitedUntil && (
        <p className="mt-2 text-[11px] text-sky-600">
          {isFollowing
            ? `Caught up with the ledger — checks for new follow-backs ${untilTime(keyword.restingUntil)}.`
            : (
              <>
                Feed resting until {untilTime(keyword.restingUntil)} so x.com can put new posts in it —
                {' '}then it reads the top {fmt(budget)} page{budget === 1 ? '' : 's'} again.
                {keyword.restMinutes ? ` Resting ${keyword.restMinutes} min between passes.` : ''}
              </>
            )}
        </p>
      )}

      {keyword.rateLimitedUntil && (
        <p className="mt-2 text-[11px] text-amber-600">
          {isFollowing ? 'Following list quota' : isFyp ? 'For You feed quota' : 'x.com search quota'} reached
          {keyword.accountUsername ? ` on @${keyword.accountUsername}` : ''} —
          {' '}resumes {untilTime(keyword.rateLimitedUntil)}
          {!isFyp && !isFollowing && keyword.accountUsername && '. Set the account to “rotate” to spread searches across every source account.'}
        </p>
      )}

      {keyword.status === 'exhausted' && (
        <p className="mt-2 text-[11px] text-amber-600">
          {isFyp
            ? 'No new usernames across several full passes — this feed keeps showing accounts already queued.'
            : 'No new usernames across several full passes — edit the keyword or retire it.'}
        </p>
      )}
      {keyword.lastError && (
        <p className="mt-2 text-[11px] text-red-600 truncate" title={keyword.lastError}>
          {keyword.lastError}
        </p>
      )}
    </div>
  );
}

function Switch({ on, disabled, onClick, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
        on ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          on ? 'translate-x-[18px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

/**
 * Follower-band, Blue, and FYP link-in-bio gates the scanner uses live.
 */
function AutoSourceProfileGates({ sessionId, filters, onUpdated }) {
  const [minFollowers, setMinFollowers] = useState(
    () => String(filters?.autoSourceMinFollowers || 0)
  );
  const [maxFollowers, setMaxFollowers] = useState(
    () => String(filters?.autoSourceMaxFollowers || 0)
  );
  const [rejectBlue, setRejectBlue] = useState(
    () => Boolean(filters?.autoSourceRejectBlue)
  );
  const [autoApprove, setAutoApprove] = useState(
    () => filters?.autoSourceAutoApprove !== false
  );
  const [requireLinks, setRequireLinks] = useState(
    () => filters?.autoSourceRequireLinks !== false
  );
  const [deepLinkCheck, setDeepLinkCheck] = useState(
    () => Boolean(filters?.deepLinkCheckEnabled)
  );
  const [platformHosts, setPlatformHosts] = useState(
    () => (Array.isArray(filters?.deepLinkPlatformHosts) && filters.deepLinkPlatformHosts.length
      ? filters.deepLinkPlatformHosts
      : DEFAULT_DEEP_LINK_PLATFORM_HOSTS)
  );
  const [platformDraft, setPlatformDraft] = useState('');
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMinFollowers(String(filters?.autoSourceMinFollowers || 0));
    setMaxFollowers(String(filters?.autoSourceMaxFollowers || 0));
    setRejectBlue(Boolean(filters?.autoSourceRejectBlue));
    setAutoApprove(filters?.autoSourceAutoApprove !== false);
    setRequireLinks(filters?.autoSourceRequireLinks !== false);
    setDeepLinkCheck(Boolean(filters?.deepLinkCheckEnabled));
    setPlatformHosts(
      Array.isArray(filters?.deepLinkPlatformHosts) && filters.deepLinkPlatformHosts.length
        ? filters.deepLinkPlatformHosts
        : DEFAULT_DEEP_LINK_PLATFORM_HOSTS
    );
  }, [
    filters?.autoSourceMinFollowers,
    filters?.autoSourceMaxFollowers,
    filters?.autoSourceRejectBlue,
    filters?.autoSourceAutoApprove,
    filters?.autoSourceRequireLinks,
    filters?.deepLinkCheckEnabled,
    filters?.deepLinkPlatformHosts,
  ]);

  const save = async (patch) => {
    if (!sessionId || saving) return;
    setSaving(true);
    try {
      const res = await api.patch(`/scraper-v2/sessions/${sessionId}/filters`, patch);
      if (res.data.success) {
        onUpdated?.(res.data.filters, res.data.pendingSources, res.data.skippedSourcesCount);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save auto-source gates');
    } finally {
      setSaving(false);
    }
  };

  const saveBand = () => save({
    autoSourceMinFollowers: Number(minFollowers) || 0,
    autoSourceMaxFollowers: Number(maxFollowers) || 0,
    autoSourceRejectBlue: rejectBlue,
  });

  const applyRecommended = () => {
    setMinFollowers(String(RECOMMENDED_AUTO_SOURCE_GATES.autoSourceMinFollowers));
    setMaxFollowers(String(RECOMMENDED_AUTO_SOURCE_GATES.autoSourceMaxFollowers));
    setRejectBlue(true);
    setRequireLinks(true);
    return save({ ...RECOMMENDED_AUTO_SOURCE_GATES });
  };

  const savePlatformHosts = (hosts) => {
    const normalized = [...new Set(hosts.map(normalizeDeepLinkPlatformHost).filter(Boolean))];
    const next = normalized.length ? normalized : [...DEFAULT_DEEP_LINK_PLATFORM_HOSTS];
    setPlatformHosts(next);
    if (deepLinkCheck) {
      save({ deepLinkPlatformHosts: next });
    }
  };

  const addPlatformHost = () => {
    const host = normalizeDeepLinkPlatformHost(platformDraft);
    if (!host) return;
    if (platformHosts.includes(host)) {
      setPlatformDraft('');
      return;
    }
    savePlatformHosts([...platformHosts, host]);
    setPlatformDraft('');
  };

  const removePlatformHost = (host) => {
    savePlatformHosts(platformHosts.filter((h) => h !== host));
  };

  const liveBand = formatAutoSourceBand({
    autoSourceMinFollowers: Number(minFollowers) || 0,
    autoSourceMaxFollowers: Number(maxFollowers) || 0,
    autoSourceRejectBlue: rejectBlue,
    autoSourceRequireLinks: requireLinks,
  });

  return (
    <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-900/60 p-3 space-y-3">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-800 dark:text-indigo-300">
            Who we accept as a source
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Keyword, For You, and follow-back discovery only — not usernames you add by hand. Link-in-bio applies to For You and Following. Change it any time; next sweep uses it.
          </p>
        </div>
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200">
          {liveBand}
        </span>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-[11px] text-gray-600 dark:text-gray-400">
          Min followers
          <input
            type="number"
            min={0}
            value={minFollowers}
            onChange={(e) => setMinFollowers(e.target.value)}
            onBlur={saveBand}
            className="mt-0.5 block w-28 text-sm px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          />
        </label>
        <label className="text-[11px] text-gray-600 dark:text-gray-400">
          Max followers
          <input
            type="number"
            min={0}
            value={maxFollowers}
            onChange={(e) => setMaxFollowers(e.target.value)}
            onBlur={saveBand}
            className="mt-0.5 block w-28 text-sm px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
          />
        </label>
        <button
          type="button"
          onClick={applyRecommended}
          disabled={saving || !sessionId}
          className="px-2 py-1.5 text-[11px] rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 shrink-0 disabled:opacity-40"
        >
          Use 3k–30k, drop Blue, link in bio
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900">
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-800 dark:text-gray-200">Reject Blue</div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Skip X Premium checkmarks</p>
          </div>
          <Switch
            on={rejectBlue}
            disabled={saving || !sessionId}
            label="Reject Blue"
            onClick={() => {
              const next = !rejectBlue;
              setRejectBlue(next);
              save({ autoSourceRejectBlue: next });
            }}
          />
        </div>
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900">
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-800 dark:text-gray-200">Auto-approve FYP finds</div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Off = you review unmatched links. OF / GetMySocial / Link.me / Beacons / Telegram still queue</p>
          </div>
          <Switch
            on={autoApprove}
            disabled={saving || !sessionId}
            label="Auto-approve FYP finds"
            onClick={() => {
              const next = !autoApprove;
              setAutoApprove(next);
              save({ autoSourceAutoApprove: next });
            }}
          />
        </div>
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900 sm:col-span-2">
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-800 dark:text-gray-200">Require link in bio</div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">For You and Following — skip accounts with no website or bio link</p>
          </div>
          <Switch
            on={requireLinks}
            disabled={saving || !sessionId}
            label="Require link in bio"
            onClick={() => {
              const next = !requireLinks;
              setRequireLinks(next);
              save({ autoSourceRequireLinks: next });
            }}
          />
        </div>
        <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900 sm:col-span-2">
          <div className="min-w-0">
            <div className="text-xs font-medium text-gray-800 dark:text-gray-200">Deep link check for platforms</div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              GET bio / Linktree / wrapper pages and require an approved platform (OnlyFans-class or Telegram). Applies to all session sources when toggled on.
            </p>
          </div>
          <Switch
            on={deepLinkCheck}
            disabled={saving || !sessionId}
            label="Deep link check for platforms"
            onClick={() => {
              const next = !deepLinkCheck;
              setDeepLinkCheck(next);
              save({
                deepLinkCheckEnabled: next,
                deepLinkPlatformHosts: platformHosts,
              });
            }}
          />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40">
        <button
          type="button"
          onClick={() => setPlatformsOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        >
          <span className="text-xs font-medium text-gray-800 dark:text-gray-200">
            Creator platforms ({platformHosts.length})
          </span>
          <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${platformsOpen ? 'rotate-180' : ''}`} />
        </button>
        {platformsOpen && (
          <div className="px-3 pb-3 space-y-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-[11px] text-gray-500 dark:text-gray-400 pt-2">
              Hostnames matched directly or found inside bio / aggregator pages.
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
              {platformHosts.map((host) => (
                <span
                  key={host}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600"
                >
                  {host}
                  <button
                    type="button"
                    aria-label={`Remove ${host}`}
                    disabled={saving || !sessionId}
                    onClick={() => removePlatformHost(host)}
                    className="text-gray-400 hover:text-rose-500 disabled:opacity-40"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={platformDraft}
                onChange={(e) => setPlatformDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addPlatformHost();
                  }
                }}
                placeholder="onlyfans.com or https://fansly.com"
                className="flex-1 text-sm px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              />
              <button
                type="button"
                onClick={addPlatformHost}
                disabled={saving || !sessionId || !platformDraft.trim()}
                className="px-2.5 py-1.5 text-[11px] rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 disabled:opacity-40"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => savePlatformHosts([...DEFAULT_DEEP_LINK_PLATFORM_HOSTS])}
                disabled={saving || !sessionId}
                className="px-2.5 py-1.5 text-[11px] rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40"
              >
                Reset defaults
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function hrefForProfileUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function PendingProfileLinks({ urls, checking }) {
  const list = (Array.isArray(urls) ? urls : []).map((u) => String(u || '').trim()).filter(Boolean);
  if (!list.length) {
    return (
      <p className="text-[11px] text-gray-400 mt-0.5">
        {checking ? 'Checking links…' : 'No link in bio'}
      </p>
    );
  }
  return (
    <div className="mt-0.5 flex flex-col gap-0.5">
      {list.map((url) => (
        <a
          key={url}
          href={hrefForProfileUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-indigo-600 dark:text-indigo-300 hover:underline truncate"
          title={url}
        >
          {prettyProfileUrl(url) || url}
        </a>
      ))}
    </div>
  );
}

function FypPendingReview({ sessionId, pending = [], onChanged, onSourcesAdded }) {
  const [busy, setBusy] = useState(false);
  const fypPending = pending.filter((p) => p.pendingReason !== 'dm_me');
  const dmMePending = pending.filter((p) => p.pendingReason === 'dm_me');
  if (!fypPending.length && !dmMePending.length) return null;

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.data.success) {
        if (ok) toast.success(ok);
        onChanged?.(res.data.pendingSources || [], res.data.heldSources);
        if (res.data.added > 0) onSourcesAdded?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const base = `/scraper-v2/sessions/${encodeURIComponent(sessionId)}/pending-sources`;

  const renderList = (items, { title, hint, storeLabel }) => (
    <div className="rounded-lg border border-amber-400/40 bg-amber-50/80 dark:bg-amber-950/20 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
            {title}
          </div>
          <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5">
            {hint}
          </p>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => run(
              () => api.post(`${base}/reject`, { usernames: items.map((p) => p.username) }),
              'Cleared the list'
            )}
            className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Skip all
          </button>
          {storeLabel && (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(
                () => api.post(`${base}/store`, { usernames: items.map((p) => p.username) }),
                `Stored ${items.length}`
              )}
              className="px-2 py-1 text-[11px] rounded-lg border border-amber-400 text-amber-800 dark:text-amber-200 disabled:opacity-40"
            >
              {storeLabel}
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => run(
              () => api.post(`${base}/approve`, { usernames: items.map((p) => p.username) }),
              `Approved ${items.length}`
            )}
            className="px-2 py-1 text-[11px] rounded-lg bg-indigo-600 text-white disabled:opacity-40"
          >
            Approve all
          </button>
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-amber-200/60 dark:divide-amber-900/40">
        {items.map((p) => (
          <div key={p.username} className="flex items-start gap-2 py-1.5 text-sm">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-gray-900 dark:text-white">@{p.username}</span>
                {p.verified === true && (
                  <span className="px-1.5 py-0 rounded text-[10px] font-semibold bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                    Blue
                  </span>
                )}
                {p.tag && (
                  <span className="text-[10px] text-gray-400">{p.tag}</span>
                )}
              </div>
              <p className="text-[11px] text-gray-500 truncate">
                {p.name || '—'} · {formatFollowerCount(p.followersCount)} followers
              </p>
              {p.bio ? (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2" title={p.bio}>
                  {p.bio}
                </p>
              ) : null}
              <PendingProfileLinks urls={p.profileUrls} checking={p.linksChecked !== true} />
            </div>
            <button
              type="button"
              disabled={busy}
              title="Skip"
              onClick={() => run(() => api.post(`${base}/reject`, { usernames: [p.username] }))}
              className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
            {storeLabel && (
              <button
                type="button"
                disabled={busy}
                title="Store as held"
                onClick={() => run(
                  () => api.post(`${base}/store`, { usernames: [p.username] }),
                  `Stored @${p.username}`
                )}
                className="px-1.5 py-1 text-[10px] rounded-lg border border-amber-400 text-amber-800 disabled:opacity-40"
              >
                Store
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              title="Approve"
              onClick={() => run(
                () => api.post(`${base}/approve`, { usernames: [p.username] }),
                `Approved @${p.username}`
              )}
              className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-40"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {fypPending.length > 0 && renderList(fypPending, {
        title: 'Approve FYP finds',
        hint: `${fypPending.length} username${fypPending.length === 1 ? '' : 's'} waiting — unmatched or unclear links. Trusted creator links are queued on their own.`,
      })}
      {dmMePending.length > 0 && renderList(dmMePending, {
        title: 'Approve — no link, DM me',
        hint: `${dmMePending.length} account${dmMePending.length === 1 ? '' : 's'} with no website and “DM me” in bio or name. Scrape, store for later, or dismiss.`,
        storeLabel: 'Store all',
      })}
    </>
  );
}

function HeldForLater({ sessionId, held = [], onChanged, onSourcesAdded }) {
  const [busy, setBusy] = useState(false);
  if (!held.length) return null;

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const res = await fn();
      if (res.data.success) {
        if (ok) toast.success(ok);
        onChanged?.(res.data.heldSources || []);
        if (res.data.added > 0) onSourcesAdded?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const base = `/scraper-v2/sessions/${encodeURIComponent(sessionId)}/held-sources`;

  return (
    <div className="rounded-lg border border-teal-400/40 bg-teal-50/80 dark:bg-teal-950/20 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-300">
            Held for later
          </div>
          <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5">
            {held.length} OF-like account{held.length === 1 ? '' : 's'} outside the scrape band — kept, not crawled. Scrape now or dismiss.
          </p>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => api.post(`${base}/dismiss`, {}), 'Dismissed held list')}
            className="px-2 py-1 text-[11px] rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            Dismiss all
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => api.post(`${base}/promote`, {}), `Queued ${held.length}`)}
            className="px-2 py-1 text-[11px] rounded-lg bg-teal-700 text-white disabled:opacity-40"
          >
            Scrape all
          </button>
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto divide-y divide-teal-200/60 dark:divide-teal-900/40">
        {held.map((p) => (
          <div key={p.username} className="flex items-start gap-2 py-1.5 text-sm">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-gray-900 dark:text-white">@{p.username}</span>
                {p.holdReason && (
                  <span className="text-[10px] text-teal-700 dark:text-teal-300">{p.holdReason}</span>
                )}
                {p.tag && <span className="text-[10px] text-gray-400">{p.tag}</span>}
              </div>
              <p className="text-[11px] text-gray-500 truncate">
                {p.displayName || '—'} · {formatFollowerCount(p.followersCount)} followers
              </p>
              {p.bio ? (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2" title={p.bio}>
                  {p.bio}
                </p>
              ) : null}
              <PendingProfileLinks urls={p.profileUrls} checking={false} />
            </div>
            <button
              type="button"
              disabled={busy}
              title="Dismiss"
              onClick={() => run(() => api.post(`${base}/dismiss`, { usernames: [p.username] }))}
              className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              disabled={busy}
              title="Scrape"
              onClick={() => run(
                () => api.post(`${base}/promote`, { usernames: [p.username] }),
                `Queued @${p.username}`
              )}
              className="p-1 rounded-lg text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-40"
            >
              <CheckIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Keyword auto-sourcing for a scrape session.
 *
 * Keywords are run through x.com search whenever the session's source queue
 * drains, so scrape accounts keep working instead of going idle. This panel
 * manages that keyword set and shows the history of ones already spent.
 */
export default function ScraperV2AutoSourcePanel({
  sessionId,
  sessionStatus,
  categories = [],
  filters,
  onFiltersUpdated,
  onSourcesAdded,
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [draftKeyword, setDraftKeyword] = useState('');
  const [tagMode, setTagMode] = useState('select');
  const [tag, setTag] = useState('');
  const [newTag, setNewTag] = useState('');
  const [product, setProduct] = useState('Top');
  const [accountId, setAccountId] = useState('');
  const [sourceMode, setSourceMode] = useState('keyword');
  const [workspaces, setWorkspaces] = useState([]);
  const [followbackLedgerId, setFollowbackLedgerId] = useState('legacy');
  const [fypAccountIds, setFypAccountIds] = useState([]);
  const [restMinutes, setRestMinutes] = useState(String(FYP_DEFAULT_REST_MINUTES));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const resolvedTag = tagMode === 'create' ? newTag.trim() : tag.trim();
  const canSubmit = sourceMode === 'fyp' || sourceMode === 'following'
    ? !!resolvedTag && fypAccountIds.length > 0
    : !!resolvedTag && !!draftKeyword.trim();

  const toggleFypAccount = (id) => setFypAccountIds((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  useEffect(() => {
    api.get('/scraper-v2/workspaces')
      .then((res) => {
        if (res.data?.success) setWorkspaces(res.data.workspaces || []);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await api.get(`/scraper-v2/sessions/${encodeURIComponent(sessionId)}/keywords`);
      if (res.data.success) setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load keywords');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // Keep the approval badge live while the crawl is scanning, even if the
  // panel is collapsed.
  useEffect(() => {
    if (sessionStatus !== 'running') return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [sessionStatus, load]);

  useEffect(() => {
    if ((data?.pendingSources || []).length > 0 || (data?.heldSources || []).length > 0) setOpen(true);
  }, [data?.pendingSources?.length, data?.heldSources?.length]);

  const active = data?.active || [];
  const history = data?.history || [];
  const accounts = useMemo(() => data?.availableAccounts || [], [data]);

  const tagOptions = useMemo(() => {
    const names = new Set(categories.map((c) => c.name).filter(Boolean));
    (data?.availableTags || []).forEach((t) => names.add(t));
    return [...names].sort();
  }, [categories, data]);

  const activeAccounts = useMemo(
    () => accounts.filter((a) => a.status === 'active'),
    [accounts]
  );

  const mutate = async (fn, successMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      if (successMessage) toast.success(successMessage);
      await load();
      return res;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Keyword update failed');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const base = `/scraper-v2/sessions/${encodeURIComponent(sessionId)}/keywords`;

  const handleAddFyp = async () => {
    const res = await mutate(
      () => api.post(
        `/scraper-v2/sessions/${encodeURIComponent(sessionId)}/fyp-sources`,
        { tag: resolvedTag, accountIds: fypAccountIds, restMinutes: Number(restMinutes) || undefined }
      )
    );
    if (!res) return;

    const { added = [], revived = [], alreadyRunning = 0 } = res.data || {};
    const started = added.length + revived.length;
    toast.success(started
      ? `Sourcing the For You feed of ${started} account${started > 1 ? 's' : ''}`
      : `Already sourcing ${alreadyRunning === 1 ? 'that feed' : 'those feeds'}`);
    setFypAccountIds([]);
    onSourcesAdded?.();
  };

  const handleAddFollowing = async () => {
    const res = await mutate(
      () => api.post(
        `/scraper-v2/sessions/${encodeURIComponent(sessionId)}/following-sources`,
        {
          tag: resolvedTag,
          accountIds: fypAccountIds,
          followbackLedgerWorkspaceId: followbackLedgerId,
        }
      )
    );
    if (!res) return;
    const { added = [], revived = [], alreadyRunning = 0 } = res.data || {};
    if (revived.length || added.some((a) => a.mode === 'following' || a.keyword)) {
      toast.success('Sourcing Following lists of follow-backs');
    } else if (added.length) {
      toast.success(
        `Added ${added.length} source account${added.length > 1 ? 's' : ''} to this follow-back walk`
      );
    } else if (alreadyRunning) {
      toast.success('Those source accounts are already on this follow-back walk');
    } else {
      toast.success('Following source unchanged');
    }
    setFypAccountIds([]);
    onSourcesAdded?.();
  };

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    if (sourceMode === 'fyp') return handleAddFyp();
    if (sourceMode === 'following') return handleAddFollowing();

    const name = draftKeyword.trim();
    if (!name || !resolvedTag) return;
    // Toasted here rather than through mutate: re-adding a retired keyword
    // brings the old row back, and saying so explains why its totals are not
    // starting from zero.
    const res = await mutate(
      () => api.post(base, { keyword: name, tag: resolvedTag, product, accountId })
    );
    if (res) {
      toast.success(res.data?.revived
        ? `Keyword "${name}" brought back from history — earlier totals kept`
        : `Keyword "${name}" added`);
      setDraftKeyword('');
      setPreview(null);
      onSourcesAdded?.();
    }
  };

  const handlePreview = async () => {
    if (!draftKeyword.trim()) return;
    setPreviewing(true);
    try {
      const res = await api.post(`${base}/preview`, { keyword: draftKeyword.trim(), product, accountId });
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Preview failed');
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleEdit = (kw, patch) => mutate(
    () => api.patch(`${base}/${kw.id}`, patch),
    patch.followingAccountIds
      ? `This walk now uses ${patch.followingAccountIds.length} source account${patch.followingAccountIds.length === 1 ? '' : 's'}`
      : 'Keyword updated — search restarts from page 1'
  );

  const handleToggle = (kw) => mutate(
    () => api.patch(`${base}/${kw.id}`, kw.mode === 'following'
      ? { enabled: !kw.enabled }
      : { enabled: !kw.enabled, resetCursor: !kw.enabled }),
    kw.enabled ? 'Keyword paused' : 'Keyword resumed'
  );

  const handleReset = (kw) => mutate(
    () => api.patch(`${base}/${kw.id}`, { resetCursor: true }),
    kw.mode === 'following'
      ? 'Follow-backs reset to 0 — finished people are forgotten, walk starts again'
      : 'Cursor reset — searches again from page 1 as soon as a scrape account is free'
  );

  const handleDelete = (kw) => mutate(
    () => api.delete(`${base}/${kw.id}`),
    'Keyword retired'
  );

  /** Fetched only when a row is expanded — the list runs to thousands of handles. */
  const handleLoadAuthors = useCallback(
    async (kw) => (await api.get(`${base}/${kw.id}/authors?limit=500`)).data,
    [base]
  );

  if (!['running', 'paused', 'stopped'].includes(sessionStatus)) return null;

  const totals = data?.totals || {};

  return (
    <div className="mb-4 rounded-xl border border-indigo-500/30 bg-indigo-500/5 dark:bg-indigo-950/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <MagnifyingGlassIcon className="w-4 h-4 text-indigo-600 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              Auto-sourcing
              {(data?.pendingSources || []).length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-200 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200">
                  {(data.pendingSources || []).length} to approve
                </span>
              )}
              {(data?.skippedSourcesCount || 0) > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-700 dark:bg-gray-700/80 dark:text-gray-300">
                  {data.skippedSourcesCount} skipped kept
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {formatAutoSourceBand(filters)}
              {active.length
                ? ` · ${active.length} active · ${fmt(totals.discoveredNew)} found`
                : ' · Find new source accounts from search, For You, or follow-backs'}
            </p>
          </div>
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4">
          <AutoSourceProfileGates
            sessionId={sessionId}
            filters={filters}
            onUpdated={(nextFilters, pendingSources, skippedSourcesCount) => {
              onFiltersUpdated?.(nextFilters);
              setData((d) => (d ? {
                ...d,
                ...(pendingSources ? { pendingSources } : {}),
                ...(typeof skippedSourcesCount === 'number' ? { skippedSourcesCount } : {}),
              } : d));
            }}
          />
          <FypPendingReview
            sessionId={sessionId}
            pending={data?.pendingSources || []}
            onChanged={(next, held) => setData((d) => (d ? {
              ...d,
              pendingSources: next,
              ...(held ? { heldSources: held } : {}),
            } : d))}
            onSourcesAdded={onSourcesAdded}
          />
          <HeldForLater
            sessionId={sessionId}
            held={data?.heldSources || []}
            onChanged={(next) => setData((d) => (d ? { ...d, heldSources: next } : d))}
            onSourcesAdded={onSourcesAdded}
          />
          <form onSubmit={handleAdd} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/60 p-3 space-y-3">
            <div className="flex gap-1 text-xs">
              {SOURCE_MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { setSourceMode(m.id); setPreview(null); }}
                  className={`px-3 py-1.5 rounded-lg border font-medium ${
                    sourceMode === m.id
                      ? 'border-indigo-500 text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30'
                      : 'border-gray-300 dark:border-gray-600 text-gray-500'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              {sourceMode === 'following'
                ? 'Reads each follow-back’s Following list for a few pages, then moves to the next unread person. Extra source accounts skip anyone already claimed. Same source gates as FYP. Out-of-band OF-like accounts are kept, not scraped.'
                : sourceMode === 'fyp'
                  ? 'Reads the For You feed of each account you pick. New authors wait for your approval unless auto-approve is on.'
                  : 'Searches x.com for a keyword and queues the authors of the matching posts as new source accounts.'}
            </p>

            {sourceMode === 'keyword' && (
              <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                <input
                  value={draftKeyword}
                  onChange={(e) => { setDraftKeyword(e.target.value); setPreview(null); }}
                  placeholder="Search keyword, e.g. onlyfans.com"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                />
                <select
                  value={product}
                  onChange={(e) => { setProduct(e.target.value); setPreview(null); }}
                  className="text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                >
                  {SEARCH_PRODUCTS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1 text-[11px]">
                <button
                  type="button"
                  onClick={() => setTagMode('select')}
                  className={`px-2 py-1 rounded-lg border ${tagMode === 'select' ? 'border-indigo-500 text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-300 dark:border-gray-600 text-gray-500'}`}
                >
                  Existing
                </button>
                <button
                  type="button"
                  onClick={() => setTagMode('create')}
                  className={`px-2 py-1 rounded-lg border ${tagMode === 'create' ? 'border-indigo-500 text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-300 dark:border-gray-600 text-gray-500'}`}
                >
                  New category
                </button>
              </div>
              {tagMode === 'select' ? (
                <select
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  className="flex-1 min-w-[10rem] text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                >
                  <option value="">Select category…</option>
                  {tagOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              ) : (
                <input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  placeholder="e.g. Gaming"
                  className="flex-1 min-w-[10rem] text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                />
              )}
            </div>

            {sourceMode === 'following' && (
              <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400">
                Follow-back ledger
                <select
                  value={followbackLedgerId}
                  onChange={(e) => setFollowbackLedgerId(e.target.value)}
                  className="mt-1 w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                >
                  {(workspaces.length ? workspaces : [{ workspaceId: 'legacy', name: 'Current data (legacy)', followBacks: 0 }]).map((w) => (
                    <option key={w.workspaceId} value={w.workspaceId}>
                      {w.name} — {(Number(w.followBacks) || 0).toLocaleString()} follow-backs
                    </option>
                  ))}
                </select>
                <span className="block mt-1 font-normal text-gray-500">
                  Keep the large current ledger selected to feed this clean sheet, then switch later.
                </span>
              </label>
            )}

            <div>
              <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                {sourceMode === 'fyp' ? (
                  <>Source accounts <span className="font-normal text-gray-400">(one feed each)</span></>
                ) : sourceMode === 'following' ? (
                  <>Source accounts <span className="font-normal text-gray-400">(each fetches a different follow-back)</span></>
                ) : (
                  <>Source account <span className="font-normal text-gray-400">(runs the search)</span></>
                )}
              </label>

              {sourceMode === 'fyp' || sourceMode === 'following' ? (
                <>
                  {/* FYP: one row per feed. Following: same picker, unioned onto one row. */}
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 divide-y divide-gray-100 dark:divide-gray-700">
                    {activeAccounts.map((a) => (
                      <label
                        key={a.id}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <input
                          type="checkbox"
                          checked={fypAccountIds.includes(a.id)}
                          onChange={() => toggleFypAccount(a.id)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-gray-700 dark:text-gray-200">@{a.username}</span>
                      </label>
                    ))}
                    {!activeAccounts.length && (
                      <p className="px-3 py-2 text-[11px] text-gray-500">
                        {loading && !data ? 'Loading source accounts…' : 'No active source accounts'}
                      </p>
                    )}
                  </div>
                  {activeAccounts.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setFypAccountIds(
                        fypAccountIds.length === activeAccounts.length ? [] : activeAccounts.map((a) => a.id)
                      )}
                      className="mt-1 text-[11px] text-indigo-600 hover:underline"
                    >
                      {fypAccountIds.length === activeAccounts.length ? 'Clear all' : 'Select all'}
                    </button>
                  )}

                  {sourceMode === 'fyp' && (
                  <label className="mt-2 flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-400">
                    Rest between passes
                    <input
                      type="number"
                      min="1"
                      max="720"
                      value={restMinutes}
                      onChange={(e) => setRestMinutes(e.target.value)}
                      className="w-16 text-sm px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                    />
                    minutes
                  </label>
                  )}
                  <p className="mt-1 text-[11px] text-gray-500">
                    {sourceMode === 'following'
                      ? 'Each account claims a different follow-back. Adding more here unions them onto this category — they will not recrawl people already finished.'
                      : `First pass walks the whole backlog. Later passes read ${FYP_REFRESH_PAGES_HINT} pages and then wait, because a feed only has new posts once X has put them there.`}
                  </p>
                </>
              ) : (
                <select
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  disabled={!accounts.length}
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 disabled:opacity-60"
                >
                  <option value="">
                    {loading && !data
                      ? 'Loading source accounts…'
                      : activeAccounts.length
                        ? `Rotate across all ${activeAccounts.length} source accounts`
                        : 'No source accounts available'}
                  </option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id} disabled={a.status !== 'active'}>
                      @{a.username}{a.status !== 'active' ? ` (${a.status})` : ''}
                    </option>
                  ))}
                </select>
              )}
              {loading && !data ? (
                <p className="mt-1 text-[11px] text-gray-500">Loading source accounts…</p>
              ) : !activeAccounts.length ? (
                <p className="mt-1 text-[11px] text-amber-600">
                  No active account carries the &ldquo;source&rdquo; tag. Add it on the Accounts page —
                  discovery runs on source accounts, never on the scrape accounts.
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap justify-between items-center gap-2">
              <p className="text-[11px] text-gray-500">
                Sources found here are tagged with the chosen category.
              </p>
              <div className="flex gap-2">
                {sourceMode === 'keyword' && (
                  <button
                    type="button"
                    disabled={previewing || !draftKeyword.trim()}
                    onClick={handlePreview}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                  >
                    {previewing ? 'Testing…' : 'Test search'}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={busy || !canSubmit}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg"
                >
                  Start auto-sourcing
                </button>
              </div>
            </div>

            {preview && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-2 text-[11px]">
                <div className="text-gray-600 dark:text-gray-300 mb-1">
                  {preview.pagesWalked} page(s) · {preview.found} unique author(s) from {preview.sightings} sighting(s)
                  {' · '}<b className="text-emerald-600">{preview.newCount} new</b>
                  {preview.account && <span className="text-gray-400"> · via @{preview.account}</span>}
                </div>

                {/* Page-by-page proof that the cursor is advancing, not replaying page 1. */}
                <div className="mb-2 space-y-0.5 text-gray-500 tabular-nums">
                  {(preview.walk || []).map((p) => (
                    <div key={p.page} className="flex gap-2">
                      <span className="w-14">page {p.page}</span>
                      <span className="w-24">{p.authors} authors</span>
                      <span className="w-28">
                        {p.alreadySeenOnEarlierPage > 0 ? `${p.alreadySeenOnEarlierPage} repeated` : 'all fresh'}
                      </span>
                      <span className={p.advanced ? 'text-emerald-600' : 'text-amber-600'}>
                        {p.advanced ? `cursor → next (${p.cursorSource})` : 'no further cursor'}
                      </span>
                    </div>
                  ))}
                </div>

                {preview.stoppedBecause && (
                  <p className="mb-2 text-amber-600">
                    Stopped after {preview.pagesWalked} page(s): {preview.stoppedBecause}
                  </p>
                )}
                <div className="flex flex-wrap gap-1">
                  {preview.users.slice(0, 24).map((u) => (
                    <span
                      key={u.username}
                      className={`px-1.5 py-0.5 rounded ${u.isNew ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}
                    >
                      @{u.username}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </form>

          {loading && !data ? (
            <p className="text-xs text-gray-500">Loading keywords…</p>
          ) : (
            <>
              {active.length > 0 && (
                <div className="space-y-2">
                  {active.map((kw) => (
                    <KeywordRow
                      key={kw.id}
                      keyword={kw}
                      busy={busy}
                      pageBudget={data?.pageBudget}
                      accounts={accounts}
                      workspaces={workspaces}
                      onEdit={handleEdit}
                      onToggle={handleToggle}
                      onReset={handleReset}
                      onDelete={handleDelete}

                      onLoadAuthors={handleLoadAuthors}
                    />
                  ))}
                </div>
              )}

              {history.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowHistory((v) => !v)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                    Keyword history ({history.length})
                  </button>
                  {showHistory && (
                    <div className="mt-2 space-y-2">
                      {history.map((kw) => (
                        <KeywordRow
                          key={kw.id}
                          keyword={kw}
                          busy={busy}
                          pageBudget={data?.pageBudget}
                          accounts={accounts}
                          workspaces={workspaces}
                      onEdit={handleEdit}
                          onToggle={handleToggle}
                          onReset={handleReset}
                          onDelete={handleDelete}

                          onLoadAuthors={handleLoadAuthors}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
